/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import React, { useContext, useMemo } from 'react'

import { Checkbox, TextField } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'

// Material Dashboard 2 PRO React TS components
import MDAvatar from 'components/MDAvatar'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import { Feedback } from 'layouts/feedback/internal/types'
import FormField from 'modules/FormField'

import { FormContext } from '../../constants'

function Investigation(): JSX.Element {
	const { user } = useAuth()
	const formContext = useContext(FormContext)
	const { localForm, setFormEdited, setLocalForm, showError } = formContext

	const STAGE = 2

	const sectionDisabled = useMemo(
		() =>
			(!ROLE_LEVEL[2].includes(user.data.role) && localForm.stage.current !== STAGE) ||
			user.data.role === 'VIEWER',
		[localForm.stage, user.data.role]
	)

	const errorVisible = localForm.stage.current >= STAGE && showError

	const handleFormUpdate = (value: string, field: keyof Feedback['form']['investigation']) => {
		setLocalForm(prev => ({
			...prev,
			form: {
				...prev.form,
				investigation: {
					...prev.form.investigation,
					[field]: value,
				},
			},
		}))
		setFormEdited(true)
	}

	return (
		<Card id="investigation" sx={{ overflow: 'visible' }}>
			<MDBox display={'flex'} flexDirection={'row'} gap={2} p={3}>
				<MDAvatar bgColor="primary" size="xs">
					4
				</MDAvatar>
				<MDTypography variant="h5" fontWeight="medium">
					Investigation
				</MDTypography>
			</MDBox>
			<MDBox component="form" pb={3} px={3}>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6} md={4}>
						<FormField
							disabled={sectionDisabled}
							variant="outlined"
							label="Date/Time"
							inputProps={{ type: 'datetime-local' }}
							value={localForm.form.investigation?.datetime ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const value = e.target.value
								handleFormUpdate(value, 'datetime')
							}}
							error={errorVisible && !localForm.form.investigation?.datetime}
							helperText={
								errorVisible && !localForm.form.investigation?.datetime
									? ' Investigation time required'
									: ''
							}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							label="Details Of Investigation"
							multiline
							disabled={sectionDisabled}
							rows={5} // Adjust number of rows for the size of the textarea
							variant="outlined"
							placeholder="Please provide a detailed description of the incident."
							InputLabelProps={{ shrink: true }}
							fullWidth
							value={localForm.form.investigation?.details ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'details')
							}}
							error={errorVisible && !localForm.form.investigation?.details}
							helperText={
								errorVisible && !localForm.form.investigation?.details
									? 'Details of investigation is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={8}>
						{/* Left Side Content (optional) */}
					</Grid>
					<Grid item xs={12} sm={4}>
						<MDBox
							display="flex"
							alignItems="center"
							justifyContent="flex-end"
							flexDirection={'row'}
						>
							<MDTypography variant="h6" color="secondary">
								Investigation Complete:
							</MDTypography>
							<Checkbox
								checked={localForm.stage.current > STAGE}
								disabled={localForm.stage.min > STAGE}
								onChange={() => {
									setLocalForm(prev => ({
										...prev,
										stage: {
											...prev.stage,
											current: localForm.stage.current > STAGE ? STAGE : STAGE + 1,
										},
									}))
									setFormEdited(true)
								}}
							/>
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default Investigation
